import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Chip, Dialog, SearchField, SegmentedControl, Select } from '../../ui';
import { FilterIcon, GridIcon, ListIcon, TableIcon } from '../../ui/icons';

export const SEARCH_DEBOUNCE_MS = 300;

/**
 * The page's own search box, for tablets and phones — a collection's, and
 * the search page's: on a desktop the shell's sidebar box is the one, so only
 * one box is ever on screen. Both write the same ?q=. Typed text is sent
 * after a pause.
 */
export function PageSearch({ query, onSearch, placeholder, label, className = 'collection-toolbar__search' }) {
  const [value, setValue] = useState(query);
  const sent = useRef(query);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  useEffect(() => {
    const q = value.trim();
    if (q === sent.current) return undefined;
    const timer = setTimeout(() => {
      sent.current = q;
      onSearchRef.current(q);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);
  // A query changed elsewhere (the sidebar's box, Back, a chip removed) shows here.
  useEffect(() => {
    if (query !== sent.current) {
      sent.current = query;
      setValue(query);
    }
  }, [query]);
  return (
    <form role="search" className={className} onSubmit={(e) => { e.preventDefault(); sent.current = value.trim(); onSearch(value.trim()); }}>
      <SearchField label={label} placeholder={placeholder} value={value} onChange={setValue} data-page-search="1" />
    </form>
  );
}

/**
 * The sort choices of the select: the server's sort keys and directions,
 * with my rating, the members' average (a shared collection) and the date I
 * saw it (a type with a verb) when the types track them.
 */
export const sortOptions = (t, hasLocation, personal = {}) => [
  { id: '', label: t('collection.sort.recent') },
  { id: 'created_at:asc', label: t('collection.sort.oldest') },
  { id: 'title:asc', label: t('collection.sort.titleAsc') },
  { id: 'title:desc', label: t('collection.sort.titleDesc') },
  { id: 'year:asc', label: t('collection.sort.yearAsc') },
  { id: 'year:desc', label: t('collection.sort.yearDesc') },
  { id: 'updated_at:desc', label: t('collection.sort.updated') },
  ...(hasLocation ? [{ id: 'location:asc', label: t('collection.sort.location') }] : []),
  ...(personal.rating ? [{ id: '@mine:desc', label: t('collection.sort.mine') }] : []),
  ...(personal.rating && personal.shared ? [{ id: '@average:desc', label: t('collection.sort.average') }] : []),
  ...((personal.verbs || []).length > 0
    ? [{ id: '@done_on:desc', label: t(`collection.sort.doneOn.${personal.verbs.length === 1 ? personal.verbs[0] : 'mixed'}`) }]
    : []),
];

/** The sort select: the common choices, then "Other field…" (every sortable field, both ways) as an option group. */
function SortSelect({ value, options, onChange, hideLabel }) {
  const { t } = useTranslation();
  const common = options.filter((option) => !option.group);
  const others = options.filter((option) => option.group);
  const option = (o) => <option key={o.id} value={o.id}>{o.label}</option>;
  return (
    <Select label={t('collection.sort.label')} hideLabel={hideLabel} value={value} onChange={(e) => onChange(e.target.value)} className="collection-toolbar__sort">
      {common.map(option)}
      {others.length > 0 && <optgroup label={t('collection.sort.otherField')}>{others.map(option)}</optgroup>}
    </Select>
  );
}

/** Group by: none, the type, the place, the decade, a series, the creators or a coded field (phase 3 §4.3). */
function GroupSelect({ value, options, onChange, hideLabel }) {
  const { t } = useTranslation();
  if (options.length <= 1) return null;
  return (
    <Select label={t('collection.group.label')} hideLabel={hideLabel} value={value} onChange={(e) => onChange(e.target.value)}
            className="collection-toolbar__group">
      {options.map((o) => <option key={o.id} value={o.id}>{hideLabel && o.id ? t('collection.group.by', { name: o.label }) : o.label}</option>)}
    </Select>
  );
}

/** "All 1 112", "Movies 800", "Series 312": the type filter, with the counts of the current search. */
function TypeChips({ types, allCount, value, onPick, className }) {
  const { t, i18n } = useTranslation();
  const number = (n) => new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(n);
  const chip = (id, label, count) => (
    <Chip key={id ?? 'all'} selected={value === id} onClick={() => onPick(id)}>
      <span>{label}</span>
      <span className="collection-chips__count">{number(count)}</span>
    </Chip>
  );
  return (
    <div role="group" aria-label={t('collection.typeTabs')} className={`collection-chips ${className || ''}`.trim()}>
      {chip(null, t('collection.allTypes'), allCount)}
      {types.map((type) => chip(type.id, type.name, type.count))}
    </div>
  );
}

/**
 * Search (tablet and phone), sort, group, view switch and type chips. Below 1024 px
 * the filters live in a sheet ("Sort and filter", over the list on a tablet,
 * a bottom sheet on a phone) with the sort and the type, and its button
 * shows how many results the choices give (phase 3 §3.1, §3.5).
 */
function CollectionToolbar({
  inventory, query, onSearch, sortValue, sortChoices, onSort, view, onView, types, allCount, type, onType, showTypes,
  filterCount = 0, total = null, filterPanel = null, groupValue = '', groupChoices = [], onGroup = () => {},
}) {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const views = [
    { id: 'grid', label: t('collection.view.grid'), icon: GridIcon },
    { id: 'list', label: t('collection.view.list'), icon: ListIcon },
    { id: 'table', label: t('collection.view.table'), icon: TableIcon },
  ];
  const activeFilters = (type ? 1 : 0) + (sortValue ? 1 : 0) + (groupValue ? 1 : 0) + filterCount;
  const showLabel = total === null ? t('collection.showResults')
    : t('collection.filters.showCount', { count: total });

  return (
    <div className="collection-toolbar">
      <div className="collection-toolbar__row">
        <PageSearch
          query={query}
          onSearch={onSearch}
          label={t('shell.searchIn', { name: inventory.name })}
          placeholder={t('collection.searchCount', { count: Number(inventory.item_count || 0) })}
        />
        <div className="collection-toolbar__controls">
          <SortSelect value={sortValue} options={sortChoices} onChange={onSort} hideLabel />
          <GroupSelect value={groupValue} options={groupChoices} onChange={onGroup} hideLabel />
          <Button variant="secondary" icon={FilterIcon} className="collection-toolbar__sheet-button" onClick={() => setSheetOpen(true)}
                  aria-haspopup="dialog">
            {t('collection.sortAndFilter')}
            {activeFilters > 0 && <Badge tone="accent" count>{activeFilters}</Badge>}
          </Button>
          <SegmentedControl options={views} value={view} onChange={onView} label={t('collection.view.label')} />
        </div>
      </div>
      {showTypes && <TypeChips types={types} allCount={allCount} value={type} onPick={onType} className="collection-toolbar__chips" />}

      <Dialog open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('collection.sortAndFilter')} size="sm"
              className="collection-sheet"
              footer={<Button onClick={() => setSheetOpen(false)}>{showLabel}</Button>}>
        <div className="dialog-form">
          <SortSelect value={sortValue} options={sortChoices} onChange={onSort} />
          <GroupSelect value={groupValue} options={groupChoices} onChange={onGroup} />
          {showTypes && (
            <div className="ui-field">
              <span className="ui-field__label">{t('collection.typeTabs')}</span>
              <TypeChips types={types} allCount={allCount} value={type} onPick={onType} className="collection-chips--wrap" />
            </div>
          )}
          {filterPanel}
        </div>
      </Dialog>
    </div>
  );
}

export default CollectionToolbar;
